import {JsonOptions, fromJson, getMappedName, getMappings, toJson} from '../../../core/data-source/json.mapper';
import {httpService} from '../../../core/services/http.service';
import {SettingsRecord} from '../../../records/settings.record';
import {Role} from '../../../records/user.record';
import {useAppContext} from '../../app/AppContext';
import {AccountSettingsRecord} from './account-settings.record';

export class AccountSettingsService {
    // settings from business template
    appSettings: SettingsRecord;
    // dispatcher settings for day
    snapshotSettings: Map<number, SettingsRecord> = new Map();
    // default dispatcher settimngs
    defaultSettings: SettingsRecord;
    // default settings metadata
    config: AccountSettingsRecord[];

    // mix app settings with default or snapshot account settings
    async getSettings(snapshotDate?: number) {
        const settings = new SettingsRecord();
        const appSettimngs = await this.getAppSettings();
        const defaultSettings = snapshotDate
            ? await this.getSnapshotSettings(snapshotDate)
            : await this.getDefaultSettings();

        const mappings = getMappings(SettingsRecord);
        mappings.forEach((value: JsonOptions, key: string) => {
            settings[key] = defaultSettings[key] ?? appSettimngs[key];
        });
        return settings;
    }

    /**
     * load appSettimngs
     */
    public async getAppSettings(): Promise<SettingsRecord> {
        if (!this.appSettings) {
            this.appSettings = await httpService
                .get('/gt/gt-api/file/mxbt.json')
                .then((response: any) => fromJson(SettingsRecord, response.data));
        }
        return this.appSettings;
    }

    async getSnapshotSettings(snapshotDate: number): Promise<SettingsRecord> {
        if (!this.snapshotSettings.has(snapshotDate)) {
            this.snapshotSettings.set(
                snapshotDate,
                this.getSettingRecord(await this.loadDispatcherSettings(snapshotDate))
            );
        }
        return this.snapshotSettings.get(snapshotDate);
    }

    async getDefaultSettings(): Promise<SettingsRecord> {
        if (!this.defaultSettings) {
            this.defaultSettings = this.getSettingRecord(await this.load());
        }
        return this.defaultSettings;
    }

    private getSettingRecord(settings: AccountSettingsRecord[]) {
        const result = fromJson(
            SettingsRecord,
            settings.reduce((acc: any, data: any) => {
                acc[data.name] = data.value;
                return acc;
            }, {})
        );
        return settings.reduce((a: SettingsRecord, setting: AccountSettingsRecord) => {
            const name = getMappedName(SettingsRecord, setting.name);
            a[name] = result[name];
            return a;
        }, new SettingsRecord());
    }

    async loadDispatcherSettings(snapshotDate: number): Promise<AccountSettingsRecord[]> {
        const metaData = await httpService
            .get('/gt/gt-api/gt_account_configuration/dispatcher_settings_new?snapshot_date=' + snapshotDate)
            .then((response: any) => response.data.rows[0].metaData);
        return metaData.map((m: any) => fromJson(AccountSettingsRecord, m));
    }

    async load(isAdmin: boolean = useAppContext()?.user.isAdmin): Promise<AccountSettingsRecord[]> {
        if (this.config) {
            return this.config;
        }
        return httpService
            .get('/gt/gt-api/gt_account_configuration/' + (isAdmin ? 'new' : 'dispatcher_settings_new'))
            .then((response: any) => {
                this.config = response.data.rows[0].metaData.map((m: any) => fromJson(AccountSettingsRecord, m));
                return this.config;
            });
    }

    async saveDispatcherSettings(settings: AccountSettingsRecord[], snapshotDate: number): Promise<any> {
        this.snapshotSettings.delete(snapshotDate);
        return httpService
            .post('/gt/gt-api/gt_account_configuration/dispatcher_settings_new', {
                metaData: settings.map((setting: AccountSettingsRecord) => toJson(setting)),
                snapshot_date: snapshotDate
            })
            .then(() => {
                this.config = null;
            });
    }

    async saveDefaults(settings: AccountSettingsRecord[], snapshotDate: number): Promise<any> {
        this.snapshotSettings.delete(snapshotDate);
        return httpService
            .post('/gt/gt-api/gt_account_configuration/dispatcher_settings_with_defaults_new', {
                metaData: settings.map((setting: AccountSettingsRecord) => toJson(setting)),
                snapshot_date: snapshotDate
            })
            .then(() => {
                this.config = null;
            });
    }

    async save(settings: AccountSettingsRecord[], isAdmin: boolean = useAppContext()?.user.isAdmin): Promise<any> {
        return httpService
            .post('/gt/gt-api/gt_account_configuration/' + (isAdmin ? 'new' : 'dispatcher_settings_new'), {
                metaData: settings.map((setting: AccountSettingsRecord) => toJson(setting))
            })
            .then(() => {
                this.config = null;
                this.defaultSettings = null;
            });
    }

    // SMTP Email
    async loadMailSettings(): Promise<AccountSettingsRecord[]> {
        return httpService
            .get(`/gt/gt-api/mailSettings`)
            .then((response: any) =>
                response.data.rows[0].metaData.map((val: any) => fromJson(AccountSettingsRecord, val))
            );
    }
    async saveMailSettings(mailSettings: AccountSettingsRecord[], snapshotDate: number): Promise<any[]> {
        console.log(mailSettings);
        return httpService
            .post(`/gt/gt-api/mailSettings`, {
                metaData: mailSettings.map((st: AccountSettingsRecord) => toJson(st)),
                snapshot_date: snapshotDate
            })
            .then((response: any) => response.data.rows);
    }

    async saveTestEmail(data: any): Promise<any> {
        return httpService
            .post('/gt/gt-api/mailSettings/sendTestEmailMessage', toJson(data), {
                transformResponse: (data: any): any => data,
                disableErrorHandling: true
            })
            .then((response: any) => {
                return response;
            });
    }
}
